import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import useSWR, { SWRConfig, type Cache } from 'swr';
import { useParams, useSearchParams } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { AUTH_CHANGED_EVENT, resolveEngineAuthContext } from '@/lib/auth';
import { useAuth } from '@/stores/auth.hooks';
import { CreditsStatement } from '@/components/dashboard/CreditsStatement';
import {
  getPricingTiers,
  tiersForVertical,
  type PricingTier,
} from '@/api/pricing';
import { getBillingAccess, activateFreeTier, createCheckout, createBillingPortal } from '@/api/billing';
import { validPaddleTransaction } from '@/lib/paddleCheckout';

/**
 * /subscribe/:vertical — the hosted subscribe page + credit wallet (funnel
 * Phases C + H), wired to the live credit-model APIs (#104).
 *
 * HYBRID model: paid tiers self-serve (Subscribe → Paddle checkout, open to
 * anyone); the free tier is the reviewed front door ("Start free" unlocks only
 * after an application is approved). Every credit number comes from the API.
 */

const SENSARRA_LANDING = 'https://sensarra.com/#/'; // apply lives on the landing
const SENSARRA_WORKBENCH = 'https://sensarra.com/#/workbench';
const OPERATOR_MAILBOX = 'mailto:bot@sensarra.com?subject=Sensarra%20Institution%20plan';

function fmtCredits(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}
const panel: CSSProperties = {
  border: '1px solid var(--sp-border)',
  borderRadius: 'var(--sp-radius-lg)',
  background: 'var(--sp-bg-elev)',
};
const eyebrow: CSSProperties = {
  fontFamily: 'var(--sp-font-mono)',
  fontSize: 11,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: 'var(--sp-accent)',
};

export function SubscribePage() {
  const { vertical = 'sensarra' } = useParams();
  const [search] = useSearchParams();
  const applied = search.get('applied') === '1';
  const { user, isAuthenticated: signedIn } = useAuth();
  const context = resolveEngineAuthContext();
  const [sessionGeneration, setSessionGeneration] = useState(0);
  useEffect(() => {
    const changed = () => setSessionGeneration((value) => value + 1);
    window.addEventListener(AUTH_CHANGED_EVENT, changed);
    window.addEventListener('storage', changed);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, changed);
      window.removeEventListener('storage', changed);
    };
  }, []);
  // The credential participates only in lifetime replacement, never in a key.
  const lifetime = useMemo(() => ({
    principal: user?.uid, verticalId: vertical, sessionGeneration,
    context: { token: context.token, activeTenantId: context.activeTenantId },
    cache: new Map(),
  }), [context.token, context.activeTenantId, user?.uid, vertical, sessionGeneration]);

  const [busyTier, setBusyTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [freeActivated, setFreeActivated] = useState(false);

  const { data: tiers, isLoading } = useSWR('/api/v1/pricing/tiers', getPricingTiers);
  const { data: access } = useSWR(signedIn ? ['billing/access', vertical] : null, () =>
    getBillingAccess(vertical),
  );

  const approved = Boolean(access?.admitted);
  const startFreeEnabled = applied || approved;
  const verticalTiers = tiers ? tiersForVertical(tiers, vertical) : [];

  const checkoutBase = `${window.location.origin}/subscribe/${vertical}`;

  const onSubscribe = async (tier: PricingTier) => {
    setError(null);
    setBusyTier(tier.tier_key);
    try {
      if (!signedIn) {
        window.location.assign(`/login?return_to=${encodeURIComponent(window.location.href)}`);
        return;
      }
      const res = await createCheckout({
        verticalId: vertical,
        tier: tier.tier_key,
        successUrl: `${checkoutBase}?checkout=success`,
        cancelUrl: `${checkoutBase}?checkout=cancel`,
      });
      if (!validPaddleTransaction(res.session_id)) throw new Error('Invalid checkout session');
      // Use our payment host even while the old account default link is being updated.
      window.location.assign(`/checkout?_ptxn=${encodeURIComponent(res.session_id)}&vertical=${encodeURIComponent(vertical)}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setError(
        /no paddle price/i.test(message)
          ? `${tier.display_name} checkout isn’t available yet — please check back soon.`
          : 'Checkout could not start. Please try again.',
      );
    } finally {
      setBusyTier(null);
    }
  };

  const onManageBilling = async () => {
    setError(null);
    setBusyTier('__portal__');
    try {
      const result = await createBillingPortal(vertical, checkoutBase);
      window.location.assign(result.url);
    } catch { setError('Billing management could not open. Please try again.'); }
    finally { setBusyTier(null); }
  };

  const onStartFree = async () => {
    setError(null);
    setBusyTier('__free__');
    try {
      if (!signedIn) {
        window.location.assign(`/login?return_to=${encodeURIComponent(window.location.href)}`);
        return;
      }
      await activateFreeTier(vertical);
      setFreeActivated(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setError(
        /(review|approv|403|400)/i.test(message)
          ? 'Your application is still in review — we’ll email you when the free tier is ready.'
          : 'Could not activate the free tier. Please try again.',
      );
    } finally {
      setBusyTier(null);
    }
  };

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '34px 24px 70px' }}>
      <div style={eyebrow}>Invited beta · platform reviewed</div>
      <h1 style={{ fontSize: 32, fontWeight: 600, margin: '10px 0 8px', color: 'var(--sp-text)' }}>
        Choose how you start
      </h1>
      <p style={{ color: 'var(--sp-text-muted)', fontSize: 15.5, maxWidth: '64ch', margin: 0, lineHeight: 1.6 }}>
        Reviewed access — the Sensarra operator approves labs before they run real data. Start on the
        free tier, or subscribe for a larger monthly credit allowance. Real compute is metered on
        credits; you decide if and when to add more.
      </p>

      {signedIn ? <p><button className="sp-btn" disabled={busyTier !== null} onClick={() => void onManageBilling()}>Manage billing</button></p> : null}

      {/* Entry banner — applied vs. not-applied */}
      {applied ? (
        <div style={{ ...panel, padding: '14px 18px', marginTop: 22, display: 'flex', gap: 12 }}>
          <span aria-hidden style={{ width: 9, height: 9, borderRadius: 999, background: 'var(--sp-ok)', marginTop: 6, flex: 'none' }} />
          <div>
            <strong style={{ color: 'var(--sp-text)' }}>Your early-access application is in review.</strong>
            <p style={{ margin: '3px 0 0', color: 'var(--sp-text-muted)', fontSize: 13.5 }}>
              We email approved labs a free-tier account with a welcome credit grant and beta access.
            </p>
          </div>
        </div>
      ) : !approved ? (
        <div style={{ ...panel, padding: '14px 18px', marginTop: 22, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Sparkles size={16} style={{ color: 'var(--sp-accent)', marginTop: 3, flex: 'none' }} />
          <div>
            <strong style={{ color: 'var(--sp-text)' }}>New to Sensarra? Start with a quick application.</strong>
            <p style={{ margin: '3px 0 8px', color: 'var(--sp-text-muted)', fontSize: 13.5 }}>
              Access is reviewed. Tell us about your lab on the home page and we’ll set you up on the
              free tier — then the plans below are yours to choose.
            </p>
            <a className="sp-btn" href={SENSARRA_LANDING} style={{ textDecoration: 'none' }}>
              Apply for early access →
            </a>
          </div>
        </div>
      ) : null}

      {signedIn ? (
        <StatementLifetime lifetime={lifetime} verticalId={vertical}
          accountName={user?.tenants.find((tenant) => tenant.tenant_id === context.activeTenantId)?.display_name || 'Selected account'} />
      ) : null}

      {error ? (
        <div role="alert" style={{ marginTop: 18, padding: '12px 16px', border: '1px solid var(--sp-error)', borderRadius: 'var(--sp-radius)', color: 'var(--sp-error)', fontSize: 13.5 }}>
          {error}
        </div>
      ) : null}
      {freeActivated ? (
        <div style={{ marginTop: 18, padding: '12px 16px', border: '1px solid var(--sp-ok)', borderRadius: 'var(--sp-radius)', color: 'var(--sp-text)', fontSize: 13.5 }}>
          Free tier activated. <a href={SENSARRA_WORKBENCH} style={{ color: 'var(--sp-accent)' }}>Open the Sensarra workbench →</a>
        </div>
      ) : null}

      {/* Tier cards */}
      {isLoading ? (
        <p style={{ marginTop: 28, color: 'var(--sp-text-muted)' }}>Loading plans…</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14, marginTop: 26 }}>
          {verticalTiers.map((tier) => {
            const isFree = tier.is_default || tier.monthly_price_usd === 0;
            const featured = isFree;
            return (
              <div
                key={tier.tier_key}
                style={{
                  ...panel,
                  padding: '24px 22px',
                  display: 'flex',
                  flexDirection: 'column',
                  ...(featured ? { borderColor: 'var(--sp-ok)', boxShadow: '0 0 0 3px color-mix(in srgb, var(--sp-ok) 12%, transparent)' } : {}),
                }}
              >
                <div className="sp-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{tier.display_name}</span>
                  {featured ? (
                    <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9.5, color: 'var(--sp-ok)', border: '1px solid var(--sp-ok)', borderRadius: 20, padding: '2px 9px' }}>start here</span>
                  ) : null}
                </div>
                <div style={{ fontSize: 34, fontWeight: 600, color: 'var(--sp-text)', margin: '12px 0 2px' }}>
                  {isFree ? '$0' : <>${tier.monthly_price_usd}<span style={{ fontSize: 14, color: 'var(--sp-text-muted)', fontWeight: 400 }}> / month</span></>}
                </div>
                <span style={{ display: 'inline-block', fontFamily: 'var(--sp-font-mono)', fontSize: 12.5, color: 'var(--sp-ok)', border: '1px solid var(--sp-ok)', borderRadius: 7, padding: '6px 10px', margin: '10px 0 4px' }}>
                  {isFree ? `${fmtCredits(tier.monthly_credits)} welcome credits` : `${fmtCredits(tier.monthly_credits)} credits / month`}
                </span>
                <div style={{ fontSize: 11.5, color: 'var(--sp-text-muted)', marginBottom: 8 }}>
                  {isFree ? 'Yours to keep · never expire' : 'Renews monthly · use-it-or-lose-it'}
                </div>
                {tier.tagline ? <div style={{ fontSize: 13, color: 'var(--sp-text-muted)', minHeight: 34 }}>{tier.tagline}</div> : null}
                <ul style={{ listStyle: 'none', padding: 0, margin: '14px 0 20px', display: 'grid', gap: 9 }}>
                  {tier.features.map((f) => (
                    <li key={f} style={{ fontSize: 13.5, display: 'flex', gap: 9, color: 'var(--sp-text)' }}>
                      <Check size={15} style={{ color: 'var(--sp-ok)', flex: 'none', marginTop: 2 }} /> {f}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: 'auto' }}>
                  {isFree ? (
                    <>
                      <button
                        type="button"
                        className="sp-btn"
                        disabled={!startFreeEnabled || busyTier === '__free__'}
                        onClick={onStartFree}
                        style={{ width: '100%', ...(startFreeEnabled ? {} : { opacity: 0.5, cursor: 'not-allowed' }) }}
                      >
                        {busyTier === '__free__' ? 'Activating…' : 'Start free'}
                      </button>
                      {!startFreeEnabled ? (
                        <div style={{ marginTop: 8, textAlign: 'center', fontSize: 11.5, color: 'var(--sp-accent)' }}>
                          Reviewed — <a href={SENSARRA_LANDING} style={{ color: 'var(--sp-accent)' }}>apply first</a> to unlock
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <button
                      type="button"
                      className="sp-btn sp-btn-primary"
                      disabled={busyTier === tier.tier_key}
                      onClick={() => onSubscribe(tier)}
                      style={{ width: '100%' }}
                    >
                      {busyTier === tier.tier_key ? 'Starting…' : 'Subscribe'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Institution — static, sales-assisted (no API tier; no hardcoded numbers). */}
          <div style={{ ...panel, padding: '24px 22px', display: 'flex', flexDirection: 'column' }}>
            <div className="sp-label">Institution</div>
            <div style={{ fontSize: 34, fontWeight: 600, color: 'var(--sp-text)', margin: '12px 0 2px' }}>Custom</div>
            <span style={{ display: 'inline-block', fontFamily: 'var(--sp-font-mono)', fontSize: 12.5, color: 'var(--sp-text-muted)', border: '1px solid var(--sp-border)', borderRadius: 7, padding: '6px 10px', margin: '10px 0 4px' }}>
              Custom credit pool
            </span>
            <div style={{ fontSize: 11.5, color: 'var(--sp-text-muted)', marginBottom: 8 }}>Billed by agreement</div>
            <div style={{ fontSize: 13, color: 'var(--sp-text-muted)', minHeight: 34 }}>
              For departments and multi-lab groups with procurement, seats, and onboarding needs.
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '14px 0 20px', display: 'grid', gap: 9 }}>
              {['Multi-lab seats & shared credit pool', 'Invoicing / procurement & onboarding', 'Priority support'].map((f) => (
                <li key={f} style={{ fontSize: 13.5, display: 'flex', gap: 9, color: 'var(--sp-text)' }}>
                  <Check size={15} style={{ color: 'var(--sp-ok)', flex: 'none', marginTop: 2 }} /> {f}
                </li>
              ))}
            </ul>
            <a className="sp-btn" href={OPERATOR_MAILBOX} style={{ marginTop: 'auto', textAlign: 'center', textDecoration: 'none' }}>
              Contact us
            </a>
          </div>
        </div>
      )}

      {/* Honest framing */}
      <div style={{ marginTop: 26, padding: '18px 22px', borderRadius: 'var(--sp-radius-lg)', background: 'var(--sp-bg-elev-2)', border: '1px solid var(--sp-border)', fontSize: 13.5, color: 'var(--sp-text-muted)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--sp-text)' }}>How access works.</strong> Paid plans self-serve —
        Subscribe → Paddle → active — open to anyone. The free tier is the reviewed front door: “Start
        free” unlocks after the Sensarra operator approves your application. Paid credits renew monthly
        and don’t roll over; the welcome credit and any promo or purchased credits never expire. Synthetic
        sample results stay free to explore; only real compute is metered.
      </div>
    </div>
  );
}

export default SubscribePage;

function StatementLifetime({ lifetime, verticalId, accountName }: {
  lifetime: { principal: string | undefined; context: ReturnType<typeof resolveEngineAuthContext>; cache: Cache };
  verticalId: string;
  accountName: string;
}) {
  const [current, setCurrent] = useState({ lifetime, generation: 0 });
  if (current.lifetime !== lifetime) {
    setCurrent({ lifetime, generation: current.generation + 1 });
  }
  if (!lifetime.principal || !lifetime.context.token || !lifetime.context.activeTenantId) {
    return <p className="sp-widget-body">Choose an account to view its credit statement.</p>;
  }
  return (
    <SWRConfig key={current.generation} value={{ provider: () => lifetime.cache }}>
      <CreditsStatement verticalId={verticalId} context={lifetime.context} accountName={accountName} />
    </SWRConfig>
  );
}
